import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { projectsApi } from "../api/projectsApi";

function IndeterminateCheckbox({ checked, indeterminate, onChange, className }) {
  return (
    <input
      type="checkbox"
      className={className}
      checked={checked}
      ref={el => { if (el) el.indeterminate = !!indeterminate; }}
      onChange={onChange}
    />
  );
}

export default function CreateTasksModal({ project, onClose, showToast }) {
  const [loading,      setLoading]      = useState(true);
  const [teams,        setTeams]        = useState([]);
  const [features,     setFeatures]     = useState([]);
  const [selectedSubs, setSelectedSubs] = useState(new Set());
  const [selectedBare, setSelectedBare] = useState(new Set());
  const [teamChecks,   setTeamChecks]   = useState(new Map());
  const [collapsed,    setCollapsed]    = useState(new Set());
  const [search,       setSearch]       = useState("");
  const [dueDate,      setDueDate]      = useState("");
  const [creating,     setCreating]     = useState(false);

  useEffect(() => {
    projectsApi.featuresWithSubs()
      .then(({ teams: t, features: f }) => {
        setTeams(t || []);
        setFeatures(f || []);
        const checks = new Map();
        (f || []).forEach(feat => {
          feat.sub_features.forEach(s => {
            checks.set(s.id, new Set(s.assigned_team_ids || []));
          });
        });
        setTeamChecks(checks);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  function featureCheckState(f) {
    if (f.sub_features.length === 0) return selectedBare.has(f.id) ? "checked" : "unchecked";
    const sel = f.sub_features.filter(s => selectedSubs.has(s.id)).length;
    if (sel === 0)                     return "unchecked";
    if (sel === f.sub_features.length) return "checked";
    return "indeterminate";
  }

  function toggleFeature(f) {
    if (f.sub_features.length === 0) {
      setSelectedBare(prev => { const n = new Set(prev); n.has(f.id) ? n.delete(f.id) : n.add(f.id); return n; });
      return;
    }
    const state  = featureCheckState(f);
    const subIds = f.sub_features.map(s => s.id);
    setSelectedSubs(prev => {
      const n = new Set(prev);
      if (state === "checked") subIds.forEach(id => n.delete(id));
      else                     subIds.forEach(id => n.add(id));
      return n;
    });
  }

  function toggleSub(sfId) {
    setSelectedSubs(prev => { const n = new Set(prev); n.has(sfId) ? n.delete(sfId) : n.add(sfId); return n; });
  }

  function toggleTeam(sfId, teamId) {
    setTeamChecks(prev => {
      const n    = new Map(prev);
      const tset = new Set(n.get(sfId) || []);
      tset.has(teamId) ? tset.delete(teamId) : tset.add(teamId);
      n.set(sfId, tset);
      return n;
    });
  }

  function toggleCollapse(fId) {
    setCollapsed(prev => { const n = new Set(prev); n.has(fId) ? n.delete(fId) : n.add(fId); return n; });
  }

  async function handleCreateAll() {
    if (!dueDate) { showToast("Please select a due date", "warn"); return; }
    if (selectedSubs.size === 0 && selectedBare.size === 0) {
      showToast("Select at least one sub-feature", "warn"); return;
    }
    const items = [];
    features.forEach(f => {
      f.sub_features.forEach(s => {
        if (!selectedSubs.has(s.id)) return;
        const checkedTeams = teamChecks.get(s.id) || new Set();
        teams.forEach(t => {
          if (!checkedTeams.has(t.id)) return;
          items.push({
            feature_name:       f.feature_name,
            sub_feature_name:   s.sub_feature_name,
            effort_hours:       s.effort_hours > 0 ? s.effort_hours : 2,
            effort_minutes:     s.effort_minutes || 0,
            team_id:            t.id,
            team_lead_name:     t.team_lead_name     || "",
            team_lead_username: t.team_lead_username || "",
          });
        });
      });
      if (f.sub_features.length === 0 && selectedBare.has(f.id)) {
        teams.forEach(t => {
          const checkedTeams = teamChecks.get(f.id) || new Set();
          if (!checkedTeams.has(t.id)) return;
          items.push({
            feature_name:       f.feature_name,
            sub_feature_name:   "",
            effort_hours:       2,
            effort_minutes:     0,
            team_id:            t.id,
            team_lead_name:     t.team_lead_name     || "",
            team_lead_username: t.team_lead_username || "",
          });
        });
      }
    });
    if (items.length === 0) { showToast("No team is checked for the selected sub-features", "warn"); return; }
    setCreating(true);
    try {
      const body = await projectsApi.bulkCreateTasks({
        customer: project.customer_name || "",
        project:  project.project_name  || "",
        due_date: dueDate,
        items,
      });
      showToast(`${body.created} task${body.created !== 1 ? "s" : ""} created ✓`);
      onClose();
    } catch (err) {
      showToast(err.message || "Failed to create tasks", "warn");
    } finally {
      setCreating(false);
    }
  }

  const selSubCount  = selectedSubs.size;
  const selFeatCount = features.filter(f => featureCheckState(f) === "checked").length;
  const q = search.trim().toLowerCase();
  const filtered = !q ? features : features.filter(f =>
    f.feature_name?.toLowerCase().includes(q) ||
    f.feature_code?.toLowerCase().includes(q) ||
    f.sub_features.some(s =>
      s.sub_feature_name?.toLowerCase().includes(q) ||
      s.sub_feature_code?.toLowerCase().includes(q)
    )
  );

  return createPortal(
    <div className="pv-overlay">
      <div className="pv-modal pv-modal--lg">
        <div className="pv-modal-header">
          <span className="pv-modal-title">Create Tasks — {project.project_name}</span>
          <button className="pv-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="pv-ct-toolbar">
          <input
            className="pv-ct-search"
            placeholder="Search features or sub-features…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <span className="pv-ct-count">
            <span className="pv-ct-count-num">{selFeatCount}</span> feature{selFeatCount !== 1 ? "s" : ""},&nbsp;
            <span className="pv-ct-count-num">{selSubCount}</span> sub-feature{selSubCount !== 1 ? "s" : ""} selected
          </span>
          <div className="pv-ct-actions">
            <label className="pv-ct-due-label">
              Due date
              <input
                type="date"
                className="pv-ct-due-input"
                value={dueDate}
                onChange={e => setDueDate(e.target.value)}
              />
            </label>
            <button
              className="uv-btn uv-btn--save pv-ct-create-btn"
              onClick={handleCreateAll}
              disabled={creating || loading}
            >
              {creating ? "Creating…" : "Create All Tasks"}
            </button>
          </div>
        </div>

        <div className="pv-ct-body">
          {loading && <div className="pv-ct-state">Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div className="pv-ct-state">{q ? "No matching features or sub-features." : "No features found."}</div>
          )}
          {!loading && filtered.length > 0 && (
            <table className="pv-ct-table">
              <colgroup>
                <col style={{ width: 36 }} />
                <col />
                <col />
                {teams.map(t => <col key={t.id} style={{ width: 96 }} />)}
              </colgroup>
              <thead>
                <tr>
                  <th className="pv-ct-th" />
                  <th className="pv-ct-th">Feature</th>
                  <th className="pv-ct-th">Sub Feature</th>
                  {teams.map(t => (
                    <th key={t.id} className="pv-ct-th pv-ct-th--team">{t.team_name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(f => {
                  const state      = featureCheckState(f);
                  const isCollapsed = collapsed.has(f.id);
                  const hasSubs    = f.sub_features.length > 0;
                  const visibleSubs = hasSubs && !isCollapsed ? f.sub_features : [];

                  if (!hasSubs || isCollapsed) {
                    return (
                      <tr key={f.id} className="pv-ct-feat-row">
                        <td className="pv-ct-td pv-ct-td--toggle">
                          {hasSubs && (
                            <button className="pv-ct-toggle-btn" onClick={() => toggleCollapse(f.id)}>▶</button>
                          )}
                        </td>
                        <td className="pv-ct-td pv-ct-td--feat">
                          <label className="pv-ct-cell-label">
                            <IndeterminateCheckbox
                              className="pv-ct-check"
                              checked={state === "checked"}
                              indeterminate={state === "indeterminate"}
                              onChange={() => toggleFeature(f)}
                            />
                            <span className="pv-ct-feat-info">
                              <span className="pv-ct-feat-name">{f.feature_name}</span>
                              <span className="pv-ct-feat-code">{f.feature_code}</span>
                            </span>
                          </label>
                        </td>
                        <td className="pv-ct-td pv-ct-td--sub-empty">—</td>
                        {teams.map(t => <td key={t.id} className="pv-ct-td pv-ct-td--team" />)}
                      </tr>
                    );
                  }

                  return visibleSubs.map((s, idx) => {
                    const isFirst = idx === 0;
                    return (
                      <tr key={s.id} className={isFirst ? "pv-ct-feat-row" : "pv-ct-sub-row"}>
                        {isFirst && (
                          <td className="pv-ct-td pv-ct-td--toggle" rowSpan={visibleSubs.length}>
                            <button className="pv-ct-toggle-btn" onClick={() => toggleCollapse(f.id)}>▼</button>
                          </td>
                        )}
                        {isFirst && (
                          <td className="pv-ct-td pv-ct-td--feat" rowSpan={visibleSubs.length}>
                            <label className="pv-ct-cell-label">
                              <IndeterminateCheckbox
                                className="pv-ct-check"
                                checked={state === "checked"}
                                indeterminate={state === "indeterminate"}
                                onChange={() => toggleFeature(f)}
                              />
                              <span className="pv-ct-feat-info">
                                <span className="pv-ct-feat-name">{f.feature_name}</span>
                                <span className="pv-ct-feat-code">{f.feature_code}</span>
                              </span>
                            </label>
                          </td>
                        )}
                        <td className="pv-ct-td pv-ct-td--sub">
                          <label className="pv-ct-cell-label">
                            <input
                              type="checkbox"
                              className="pv-ct-check"
                              checked={selectedSubs.has(s.id)}
                              onChange={() => toggleSub(s.id)}
                            />
                            <span className="pv-ct-sub-info">
                              <span className="pv-ct-sub-name">{s.sub_feature_name}</span>
                              <span className="pv-ct-sub-code">{s.sub_feature_code}</span>
                            </span>
                          </label>
                        </td>
                        {teams.map(t => (
                          <td key={t.id} className="pv-ct-td pv-ct-td--team">
                            <input
                              type="checkbox"
                              className="pv-ct-check"
                              checked={teamChecks.get(s.id)?.has(t.id) ?? false}
                              onChange={() => toggleTeam(s.id, t.id)}
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  });
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="pv-modal-footer">
          <button className="uv-btn uv-btn--cancel" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
